import { makeStyles } from '@material-ui/core'

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100vw',
    height: '100%',
    backgroundColor: theme.palette.background.default,
    display: 'flex',
    // Same reversal as the credentials screen — see userInfo.style.tsx for why reversing the
    // row composes with `dir` rather than cancelling against it.
    //
    // Applied here too because the card must not change sides mid-login. `nextStep` sends a
    // user to this screen from step 1 ('UPDATE_PHONE_NUMBER' / 'VERIFY_2FA'), and with a
    // plain `row` the card jumped to the opposite edge between submitting credentials and
    // entering the code.
    flexDirection: 'row-reverse',
    [theme.breakpoints.down('sm')]: {
      display: 'grid',
      gridTemplateRows: '1fr'
    }
  },
  logoPaper: {
    position: 'relative',
    flex: 1,
    height: '100%',
    paddingLeft: '12vw',
    paddingRight: '12vw',
    marginTop: 'auto',
    marginBottom: 'auto',
    display: 'flex',
    alignItems: 'center',
    [theme.breakpoints.down('sm')]: {
      padding: 0,
      position: 'absolute',
      width: '150%'
    }
  },
  logoImgSmall: {
    // In flow at the top of the card, at every breakpoint. It used to be
    // `position: fixed; top: 58; left: 58; zIndex: 5`, which placed it in a coordinate
    // system the card knew nothing about. That was harmless only while the card sat on the
    // opposite side; once `row-reverse` moved the card to the leading edge, the two claimed
    // the same corner and the logo painted over the card.
    //
    // The overlap was systematic rather than a breakpoint edge case: a percentage margin
    // resolves against the container's WIDTH, so the card's top was ~0.05W while the logo's
    // bottom was 58px + ~0.043W. The logo wins for any width below roughly 7800px.
    //
    // Nothing here is a physical property, so jss-rtl has nothing to flip and the rule
    // behaves identically in both directions — which is the point: the previous `left: 58`
    // was mirrored to `right: 58` in Persian, so the bug followed the card to the other
    // corner instead of staying put where it could be noticed once.
    display: 'block',
    width: 180,
    maxWidth: '55%',
    height: 'auto',
    margin: '0 auto 24px',
    [theme.breakpoints.down('lg')]: {
      width: 150
    },
    [theme.breakpoints.down('xs')]: {
      width: 130,
      marginBottom: 16
    }
  },
  loginPaper: {
    width: 650,
    margin: '5% 80px',
    background: theme.palette.primary.contrastText,
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.25)',
    borderRadius: 8,
    position: 'relative',
    overflowX: 'hidden',

    [theme.breakpoints.down('lg')]: {
      width: '35%',
      margin: '2% 80px 0 80px',
      height: '90%'
    },

    [theme.breakpoints.down('md')]: {
      width: 650,
      margin: 'auto',
      right: '50%',
      transform: 'translateX(50%)',
      marginBottom: 45
    },
    [theme.breakpoints.down('sm')]: {
      width: '100%',
      height: '100%'
    }
  },
  formContainer: {
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    flexDirection: 'column',
    paddingTop: '0vh',
    paddingLeft: 80,
    paddingRight: 80,
    zIndex: 1,
    '&>*': {
      zIndex: 1
    },
    [theme.breakpoints.down('lg')]: {
      paddingTop: '2vh'
    },
    [theme.breakpoints.down('md')]: {
      paddingLeft: 80,
      paddingRight: 80
    },
    [theme.breakpoints.down('xs')]: {
      justifyContent: 'flex-start',
      paddingLeft: 24,
      paddingRight: 24,
      // Was 95, which is what it took to clear the logo while the logo was
      // `position: fixed; top: 15` and therefore out of flow. The logo is now the first
      // item in this column and brings its own spacing, so keeping 95 would have stacked
      // an empty 95px band on top of it on phones.
      paddingTop: 32
    }
  },
  logoImg: {
    width: '100%',
    margin: 'auto',
    height: 'auto',
    maxHeight: '100%',
    [theme.breakpoints.down('lg')]: {
      width: '110%'
    },
    [theme.breakpoints.down('md')]: {
      display: 'none'
    }
  },
  titleSection: {
    marginBottom: 24,
    textAlign: 'center',
    zIndex: 1
  },
  title: {
    color: theme.palette.text.primary,
    fontWeight: 'bold',
    [theme.breakpoints.down('lg')]: {
      fontSize: '1.5rem'
    }
  },
  subtitle: {
    marginTop: 12,
    fontWeight: 600
  },
  inputContainer: {
    width: '100%',
    marginBottom: 10,
    '& input': {
      color: theme.palette.text.secondary,
      borderColor: theme.palette.grey[400],
      opacity: 0.8,
      '&:focus': {
        color: theme.palette.text.secondary,
        borderColor: theme.palette.grey[600],
        opacity: 1
      }
    },
    '& label': {
      color: theme.palette.text.secondary,
      borderColor: theme.palette.grey[400]
    },
    [theme.breakpoints.down('sm')]: {
      marginBottom: 0,
      marginTop: 0
    }
  },
  checkboxText: {
    color: theme.palette.text.secondary
  },
  checkbox: {
    color: theme.palette.primary.main
  },
  checkboxRoot: {
    '&$checkboxChecked': {
      color: theme.palette.primary.main
    }
  },
  checkboxChecked: {},
  loginButtonContainer: {
    marginTop: 10
  },
  loginButtonText: {
    padding: 4
  },
  loginButton: {
    backgroundColor: theme.palette.primary.main,
    marginBottom: 12,
    color: theme.palette.primary.contrastText,
    marginTop: 12
  },
  ellipseIcon: {
    position: 'absolute',
    top: 0,
    left: 0
  },
  copyrightContainer: {
    width: '100%',
    position: 'fixed',
    textAlign: 'center',
    bottom: 10,
    paddingTop: 30,
    left: 0,
    [theme.breakpoints.down('md')]: {
      textAlign: 'center',
      left: '50%',
      transform: 'translateX(-50%)'
    },
    [theme.breakpoints.down('sm')]: {
      left: '50%',
      transform: 'translateX(-50%) scale(0.9)'
    }
  },
  captchaContainer: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center'
  },
  BottomCurve: {
    position: 'absolute',
    height: '90%',
    bottom: -75,
    zIndex: -1
  },
  emailInput: {
    backgroundColor: theme.palette.background.default
  },
  passwordInput: {
    backgroundColor: theme.palette.background.default
  },
  backButton: {
    display: 'flex',
    justifyContent: 'center',
    color: theme.palette.info.main,
    cursor: 'pointer'
  },
  circularProgress: {
    marginLeft: 8,
    color: theme.palette.primary.main
  }
}))

export default useStyles
